import { Badge, Group, Paper, Progress, Stack, Text, Title } from '@mantine/core';
import {
  IconCalendarCheck,
  IconClockHour4,
  IconMeeple,
  IconKey,
  IconShoppingBag,
  IconSparkles,
} from '@tabler/icons-react';
import {
  SUBSCRIPTION_PLAN_LABELS,
  TRIAL_DURATION_DAYS,
  formatDaysLeft,
  type AccessState,
  type ContentAvailability,
} from '@creativitas/shared';
import { PlanOfferButtons, useOffer } from './PlanOffer';

/**
 * Reusable views of the entitlement state (§11, §12, §13).
 *
 * The dashboard, the profile and the admin screen all show the same facts, so
 * they share these components instead of formatting the state three times.
 */

const formatDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

function Tile({
  icon,
  label,
  children,
  tone = 'neutral',
  testId,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
  tone?: 'neutral' | 'accent' | 'gold';
  /** Stable handle for tests — the visible label is Russian prose. */
  testId: string;
}) {
  const background =
    tone === 'accent'
      ? 'var(--surface-accent-soft)'
      : tone === 'gold'
        ? 'var(--surface-gold-soft)'
        : 'var(--surface-card)';

  return (
    <Paper
      withBorder
      p="md"
      radius="lg"
      data-testid={testId}
      style={{ borderColor: 'var(--border-subtle)', background }}
    >
      <Stack gap={8}>
        <Group gap={8} c="var(--text-muted)">
          {icon}
          <Text size="xs" fw={600} tt="uppercase" lts="0.06em">
            {label}
          </Text>
        </Group>
        {children}
      </Stack>
    </Paper>
  );
}

export function TrialTile({
  trial,
  subscriptionActive,
}: {
  trial: AccessState['trial'];
  /** Передаётся там, где рядом стоит плитка тарифа: без тарифа подсказываем следующий шаг. */
  subscriptionActive?: boolean;
}) {
  /*
    Пробный период применяется не ко всем (§11). Обратный отсчёт над купленным
    продуктом читается как срок, после которого доступ закроется, — поэтому
    здесь он не показывается вовсе, а не показывается «истёкшим».
  */
  if (trial.exempt) {
    return (
      <Tile icon={<IconClockHour4 size={15} />} label="Пробный период" testId="tile-trial">
        <Text fw={600}>Не применяется</Text>
        <Text size="sm" c="var(--text-muted)">
          Доступ определяется тем, что вам открыто, и сроком не ограничен.
        </Text>
      </Tile>
    );
  }

  if (!trial.started) {
    return (
      <Tile icon={<IconClockHour4 size={15} />} label="Пробный период" testId="tile-trial">
        <Text fw={600}>Ещё не начинался</Text>
        <Text size="sm" c="var(--text-muted)">
          Отсчёт стартует при первом входе.
        </Text>
      </Tile>
    );
  }

  if (!trial.active) {
    return (
      <Tile icon={<IconClockHour4 size={15} />} label="Пробный период" testId="tile-trial">
        <Text fw={600}>Закончился</Text>
        <Text size="sm" c="var(--text-muted)">
          {formatDate(trial.endsAt)}
        </Text>
        {subscriptionActive === false ? (
          <Text size="sm" data-testid="trial-next-step">
            Продолжить работу с материалами можно по тарифу Лайт или Про — кнопка в плитке «Тариф».
          </Text>
        ) : null}
      </Tile>
    );
  }

  const used = TRIAL_DURATION_DAYS - (trial.daysLeft ?? 0);
  const percent = Math.min(100, Math.max(0, (used / TRIAL_DURATION_DAYS) * 100));

  return (
    <Tile icon={<IconClockHour4 size={15} />} label="Пробный период" tone="accent" testId="tile-trial">
      {/* §11 — «Пробный период: осталось 11 дней» */}
      <Text fw={600} fz="lg">
        Осталось {formatDaysLeft(trial.daysLeft ?? 0)}
      </Text>
      <Progress value={percent} color="teal" size="sm" radius="xl" />
      <Text size="xs" c="var(--text-muted)">
        До {formatDate(trial.endsAt)}
      </Text>
    </Tile>
  );
}

/** Тариф Лайт/Про (docs/PAYMENTS.md, §10). Про включает всё из Лайт. */
export function SubscriptionTile({ subscription }: { subscription: AccessState['subscription'] }) {
  const active = subscription.active && subscription.plan !== null;
  return (
    <Tile
      icon={<IconCalendarCheck size={15} />}
      label="Тариф"
      tone={active ? 'gold' : 'neutral'}
      testId="tile-subscription"
    >
      <Group gap="xs">
        <Text fw={600} fz="lg">
          {active ? SUBSCRIPTION_PLAN_LABELS[subscription.plan!] : 'Нет тарифа'}
        </Text>
        {active ? (
          <Badge size="sm" color="gold" variant="light">
            до {formatDate(subscription.endsAt)}
          </Badge>
        ) : null}
      </Group>
      <Text size="sm" c="var(--text-muted)">
        {active
          ? subscription.plan === 'pro'
            ? 'Открыты материалы тарифов Лайт и Про.'
            : 'Открыты материалы тарифа Лайт.'
          : subscription.expired && subscription.lastPlan
            ? `Тариф ${SUBSCRIPTION_PLAN_LABELS[subscription.lastPlan]} закончился ${formatDate(subscription.endsAt)}. Бесплатное и купленное остаётся.`
            : 'Тарифы Лайт и Про открывают материалы по подписке.'}
      </Text>
      <PlanOfferButtons subscription={subscription} />
    </Tile>
  );
}

export function OwnedGamesTile({ games }: { games: AccessState['ownedGames'] }) {
  /* Игра продаётся, только если для неё заведён товар; иначе её выдаёт администратор. */
  const offer = useOffer();
  const forSale = Boolean(offer.data?.visible) && (offer.data?.purchasableGameIds.length ?? 0) > 0;
  return (
    <Tile icon={<IconMeeple size={15} />} label="Купленные игры" testId="tile-owned-games">
      {games.length === 0 ? (
        <>
          <Text fw={600}>Нет</Text>
          <Text size="sm" c="var(--text-muted)">
            {forSale
              ? 'Игру можно купить на её странице в разделе «Игры». Купленная игра не требует тарифа.'
              : 'Владение игрой выдаёт администратор. Оно не требует тарифа.'}
          </Text>
        </>
      ) : (
        <Stack gap={6}>
          {games.map((game) => (
            <Group key={game.gameId} gap="xs">
              <IconSparkles size={15} color="var(--gold)" />
              <Text fw={600}>{game.title}</Text>
            </Group>
          ))}
          <Text size="xs" c="var(--text-muted)">
            Ведение этих игр не требует тарифа.
          </Text>
        </Stack>
      )}
    </Tile>
  );
}

/**
 * Отдельно открытые материалы: купленные поштучно и открытые администратором.
 * Купленное — своей плиткой: человек заплатил и должен видеть, за что.
 */
export function ManualGrantsTile({ grants }: { grants: AccessState['manualGrants'] }) {
  const purchased = grants.filter((grant) => grant.purchased);
  const granted = grants.filter((grant) => !grant.purchased);
  const list = (items: AccessState['manualGrants']) => (
    <Stack gap={4}>
      {items.map((grant) => (
        <Text key={grant.entitlementId} size="sm">
          {grant.title ?? grant.contentId}
        </Text>
      ))}
    </Stack>
  );

  return (
    <>
      {purchased.length > 0 ? (
        <Tile icon={<IconShoppingBag size={15} />} label="Куплено навсегда" testId="tile-purchased-content">
          {list(purchased)}
        </Tile>
      ) : null}
      {granted.length > 0 ? (
        <Tile icon={<IconKey size={15} />} label="Открыто администратором" testId="tile-manual-grants">
          {list(granted)}
        </Tile>
      ) : null}
    </>
  );
}

const KIND_LABELS: Record<ContentAvailability['kind'], string> = {
  deck: 'Колоды',
  spread: 'МАК-сессии',
  training_material: 'Обучение',
  game: 'Игры',
};

/** Makes the access engine visible: how much of each kind this user can open (§16). */
export function AvailabilitySummary({ items }: { items: ContentAvailability[] }) {
  return (
    <Paper withBorder p="md" radius="lg" style={{ borderColor: 'var(--border-subtle)' }}>
      <Stack gap="sm">
        <Title order={4}>Что вам доступно</Title>
        <Stack gap="xs">
          {items.map((item) => {
            const percent = item.total === 0 ? 0 : (item.accessible / item.total) * 100;
            const full = item.accessible === item.total && item.total > 0;

            return (
              <Stack key={item.kind} gap={4}>
                <Group justify="space-between" gap="xs">
                  <Text size="sm">{KIND_LABELS[item.kind]}</Text>
                  <Text size="sm" c={full ? 'var(--accent)' : 'var(--text-muted)'} fw={600}>
                    {item.accessible} из {item.total}
                  </Text>
                </Group>
                <Progress value={percent} color={full ? 'teal' : 'gold'} size="xs" radius="xl" />
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </Paper>
  );
}
